/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useState, Suspense, useEffect } from 'react';
import BlogHero from './components/BlogHero';
import BlogList from './components/BlogList';
import BlogNewsletter from './components/BlogNewsletter';
import FeaturedArticle from './components/FeaturedArticle';
import { blogPosts } from '../../mocks/blogPosts';
import { useSEOHead } from '../../hooks/useSEOHead';
import Breadcrumb from '../../components/feature/Breadcrumb';
import SearchInput from '../../components/base/SearchInput';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import ErrorBoundary, { retryLazy } from '../../components/base/ErrorBoundary';

const BlogSidebar = retryLazy(() => import('./components/BlogSidebar'));

const BASE = 'https://rockhoundauto.com';
const OG_IMAGE = 'https://static.readdy.ai/image/b3efdc45e89a2660551e85a9fdfadec5/a59b3ad9d14210b4ce617fff8e7523fb.png';

function SidebarSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="bg-white rounded-2xl border border-[#E8E2D6] p-6">
        <div className="h-5 bg-gray-200 rounded w-1/2 mb-4"></div>
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-16 bg-gray-100 rounded-xl mb-3"></div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-[#E8E2D6] p-6">
        <div className="h-5 bg-gray-200 rounded w-2/3 mb-4"></div>
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-3 mb-3">
            <div className="w-14 h-14 bg-gray-200 rounded-lg flex-shrink-0"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-3 bg-gray-100 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-[#E8E2D6] p-6">
        <div className="h-5 bg-gray-200 rounded w-1/3 mb-4"></div>
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-10 bg-gray-100 rounded-lg mb-2"></div>
        ))}
      </div>
    </div>
  );
}

export default function BlogPage() {
  useSEOHead({
    title: 'Car Buying Blog & Oregon Coast Auto News | Rockhound Auto',
    description: 'Expert car buying advice, Oregon Coast vehicle guides, dealer tactics exposed, and industry news. Learn how to save thousands on your next used car purchase.',
    keywords: 'car buying blog, Oregon Coast auto news, used car tips, dealer tactics, vehicle recalls, EV news, automotive industry',
    canonicalUrl: 'https://rockhoundauto.com/blog',
    dateModified: '2025-01-15',
    ogType: 'website',
    ogImage: 'https://public.readdy.ai/ai/img_res/0fc80c61196309d757e2bd00608aeadf.jpg'
  });

  // Inject structured data schemas + first-post image preload
  useEffect(() => {
    // ── Preload first post hero image for LCP ──
    const firstImage = blogPosts[0]?.image || blogPosts[0]?.heroImage;
    let preloadLink: HTMLLinkElement | null = null;
    if (firstImage) {
      preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = firstImage;
      (preloadLink as any).fetchPriority = 'high';
      document.head.appendChild(preloadLink);
    }

    injectJsonLd('schema-blog-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE}/blog` },
      ],
    });

    injectJsonLd('schema-blog-collection', {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      '@id': `${BASE}/blog#blog`,
      name: 'The Rockhound Journal',
      description:
        'Honest car-buying guides, dealership comparisons, and Pacific Northwest auto market insights from the team at Rockhound Auto in Warrenton, Oregon.',
      url: `${BASE}/blog`,
      image: {
        '@type': 'ImageObject',
        url: OG_IMAGE,
        width: 1200,
        height: 630,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Rockhound Auto',
        url: BASE,
        logo: {
          '@type': 'ImageObject',
          url: OG_IMAGE,
        },
      },
    });

    injectJsonLd('schema-blog-article-list', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${BASE}/blog#article-list`,
      name: 'Rockhound Auto Blog Articles',
      itemListElement: blogPosts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'BlogPosting',
          '@id': `${BASE}/blog/${post.slug}`,
          headline: post.title,
          description: post.excerpt,
          url: `${BASE}/blog/${post.slug}`,
          datePublished: post.date,
          dateModified: post.date,
          image: {
            '@type': 'ImageObject',
            url: post.heroImage,
            width: 1200,
            height: 600,
          },
          author: {
            '@type': 'Organization',
            name: post.author,
            url: BASE,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Rockhound Auto',
            url: BASE,
            logo: {
              '@type': 'ImageObject',
              url: OG_IMAGE,
            },
          },
          keywords: post.tags.join(', '),
          articleSection: post.category,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'Blog', '@id': `${BASE}/blog#blog` },
        },
      })),
    });

    return () => {
      preloadLink?.remove();
      removeJsonLd('schema-blog-breadcrumb');
      removeJsonLd('schema-blog-collection');
      removeJsonLd('schema-blog-article-list');
    };
  }, []);

  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const uniqueCategories = ['all', ...Array.from(new Set(blogPosts.map(p => p.category)))];

  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch =
      !searchQuery ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = blogPosts.find((p) => p.featured) ?? blogPosts[0];
  const isUnfiltered = selectedCategory === 'all' && !searchQuery;
  const listPosts = isUnfiltered
    ? filteredPosts.filter((p) => p.slug !== featuredPost?.slug)
    : filteredPosts;

  return (
    <>
      <Breadcrumb items={[{ label: 'Blog' }]} />
      <BlogHero />

      {/* Editorial Intro */}
      <div className="bg-white border-b border-[#E8E2D6] py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            Welcome to the Rockhound Journal, where we share honest insights about car buying, auto brokering, and vehicle ownership. Our team writes from real-world experience helping buyers across the Pacific Northwest navigate dealership tactics, financing options, and the true cost of vehicle ownership.
          </p>
        </div>
      </div>

      {/* What You'll Find Here */}
      <div className="bg-[#F7F5F0] py-10 sm:py-14 border-b border-[#E8E2D6]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Oswald'] text-2xl sm:text-3xl font-bold text-[#1C2B1E] text-center mb-8 sm:mb-10">
            What You'll Find Here
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-guide-line', title: 'Buyer Guides', desc: 'Step-by-step advice on financing, trade-ins, and negotiating with dealers. We break down complex processes into actionable steps that save you time and money.' },
              { icon: 'ri-scales-line', title: 'Comparisons', desc: 'Honest side-by-side comparisons of dealerships, financing options, and buying methods. We expose hidden fees and markup tactics so you can make informed decisions.' },
              { icon: 'ri-map-pin-line', title: 'City Broker Guides', desc: 'Local market insights for Portland, Seattle, Bend, Boise, Denver, and beyond. Learn how auto brokers work in your area.' },
              { icon: 'ri-newspaper-line', title: 'Industry News', desc: 'Updates on market trends, inventory shifts, and regulatory changes that affect car buyers. We translate industry jargon into plain English.' },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-xl border border-[#E8E2D6] p-6">
                <div className="w-12 h-12 bg-[#E8B84B]/10 rounded-full flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-2xl text-[#E8B84B]`}></i>
                </div>
                <h3 className="font-['Oswald'] text-lg font-bold text-[#1C2B1E] mb-3">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-[#F7F5F0] min-h-screen py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search & Filter Bar */}
          <div className="bg-white rounded-2xl border border-[#E8E2D6] p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col gap-4">
              <SearchInput
                id="blog-search"
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search articles…"
                ariaLabel="Search blog articles"
              />
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                {uniqueCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    aria-label={category === 'all' ? 'Show all articles' : `Filter by ${category}`}
                    className={`flex-shrink-0 px-4 sm:px-6 py-2.5 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      selectedCategory === category
                        ? 'bg-[#1C2B1E] text-white'
                        : 'bg-[#F7F5F0] text-gray-700 hover:bg-[#E8E2D6]'
                    }`}
                  >
                    {category === 'all' ? 'All Articles' : category}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="mb-4 sm:mb-6">
                <h2 className="font-['Oswald'] text-xl sm:text-2xl font-bold text-[#1C2B1E]">
                  {filteredPosts.length} {filteredPosts.length === 1 ? 'Article' : 'Articles'} Found
                </h2>
              </div>
              {filteredPosts.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#E8E2D6] p-8 sm:p-12 text-center">
                  <i className="ri-article-line text-6xl text-gray-300 mb-4 block"></i>
                  <h3 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E] mb-2">No articles found</h3>
                  <p className="text-gray-600 mb-6">Try adjusting your search or filter to see more results</p>
                  <button
                    onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
                    aria-label="Clear all filters"
                    className="px-6 py-3 bg-[#1C2B1E] text-white rounded-full font-medium hover:bg-[#2d4430] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <>
                  {isUnfiltered && featuredPost && (
                    <FeaturedArticle post={featuredPost} />
                  )}
                  <BlogList posts={listPosts} />
                </>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-32">
                <ErrorBoundary fallback={<SidebarSkeleton />}>
                  <Suspense fallback={<SidebarSkeleton />}>
                    <BlogSidebar />
                  </Suspense>
                </ErrorBoundary>
              </div>
            </div>
          </div>

          {/* Why Read Section */}
          <div className="mt-10 sm:mt-12 mb-8">
            <div className="bg-white rounded-2xl border border-[#E8E2D6] p-6 sm:p-8">
              <h2 className="font-['Oswald'] text-xl sm:text-2xl font-bold text-[#1C2B1E] mb-4">
                Why Read the Rockhound Journal?
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                Unlike dealership blogs that exist solely to drive traffic, we write from the perspective of buyers, not sellers. Our team has brokered hundreds of vehicle purchases across Oregon, Washington, Idaho, and Colorado, and we've seen every dealership tactic, financing trap, and hidden fee imaginable.
              </p>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Every article is written by our in-house team — not outsourced content farms — and reflects real experiences from our daily work helping buyers navigate the car-buying process. We don't accept sponsored posts or dealership advertising, so our recommendations are genuinely unbiased.
              </p>
            </div>
          </div>

          {/* About Authors */}
          <div className="mb-10 sm:mb-12">
            <div className="bg-gradient-to-br from-[#1C2B1E] to-[#2d4430] rounded-2xl p-6 sm:p-8 text-white">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Oswald'] text-2xl sm:text-3xl font-bold">Meet the 2 Bald Guys</h3>
                <a 
                  href="/about-2-bald-guys" 
                  className="text-[#E8B84B] hover:text-[#d4a43d] font-medium text-sm flex items-center gap-1 transition-colors whitespace-nowrap cursor-pointer"
                  aria-label="Learn more about the 2 Bald Guys"
                >
                  Full Story <i className="ri-arrow-right-line"></i>
                </a>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Dave - Chief Skeptic */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 bg-[#E8B84B] rounded-full flex items-center justify-center flex-shrink-0 text-3xl">
                      🧑‍🦲
                    </div>
                    <div>
                      <h4 className="font-['Oswald'] text-xl font-bold text-white mb-1">Dave</h4>
                      <p className="text-[#E8B84B] text-sm font-medium">Chief Skeptic</p>
                    </div>
                  </div>
                  <p className="text-sm text-white/90 leading-relaxed mb-3">
                    Former dealership finance manager turned buyer advocate. Dave spent 12 years in F&I offices before switching sides. Now he exposes every markup, add-on, and pressure tactic dealers use.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium">Dealer Tactics Expert</span>
                    <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium">Finance Guru</span>
                    <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium">BS Detector</span>
                  </div>
                </div>

                {/* Mike - Head of Honesty */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 bg-[#E8B84B] rounded-full flex items-center justify-center flex-shrink-0 text-3xl">
                      🧑‍🦲
                    </div>
                    <div>
                      <h4 className="font-['Oswald'] text-xl font-bold text-white mb-1">Mike</h4>
                      <p className="text-[#E8B84B] text-sm font-medium">Head of Honesty</p>
                    </div>
                  </div>
                  <p className="text-sm text-white/90 leading-relaxed mb-3">
                    Licensed auto broker and automotive journalist with 15+ years covering the Pacific Northwest market. Mike translates industry jargon into plain English and calls out bad deals when he sees them.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium">Market Analyst</span>
                    <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium">Truth Teller</span>
                    <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium">Buyer Champion</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-sm text-white/80 leading-relaxed">
                  <strong className="text-white">Why trust two bald guys?</strong> Because we've been on both sides of the desk. We know how dealerships operate, what they hide, and how buyers get taken advantage of. Our mission: arm you with the truth so you never overpay again.
                </p>
              </div>
            </div>
          </div>

          <BlogNewsletter />
        </div>
      </div>
    </>
  );
}
